import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { QuantityControl } from "@/components/ui/QuantityControl";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPrice } from "@/utils/money";
import { TruncateTooltip } from "@repo/design-system";
import { Package, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { ProductThumbnailImage } from "../../ProductThumbnailImage";
import type { VariantGridRow } from "../useVariantGridData";

export interface VariantOrderTabProps {
  rows: VariantGridRow[];
  isLoading: boolean;
  isError: boolean;
  searchPlaceholder: string;
  /** Map of variantId -> quantity, owned by the dialog. */
  quantities: Record<string, number>;
  onQuantityChange: (variantId: string, quantity: number) => void;
  /**
   * Image shown for each variant row. Variants have no per-variant image in the
   * data today, so this is the product thumbnail; it upgrades automatically if
   * the row gains its own image.
   */
  productThumbnail: string | null;
}

/**
 * Fixed track widths (not `auto`): each row is its own grid, so content-sized
 * columns would resize per row and per state — typing a quantity turns the
 * subtotal from "—" into a price and would shove the quantity box sideways
 * mid-edit. Fixed tracks keep every row and the header on the same rails.
 */
const GRID_COLS =
  "grid grid-cols-[minmax(0,1fr)_5rem_5rem_5rem_5rem] items-center gap-3 px-4";

function VariantRowSkeleton() {
  return (
    <div className={`${GRID_COLS} py-3`}>
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-8 rounded-md" />
        <Skeleton className="h-4 w-40" />
      </div>
      <Skeleton className="h-5 w-16 justify-self-center" />
      <Skeleton className="h-4 w-12 justify-self-center" />
      <Skeleton className="h-6 w-20 justify-self-center" />
      <Skeleton className="h-4 w-12 justify-self-end" />
    </div>
  );
}

export function VariantOrderTab({
  rows,
  isLoading,
  isError,
  searchPlaceholder,
  quantities,
  onQuantityChange,
  productThumbnail
}: VariantOrderTabProps) {
  const [search, setSearch] = useState("");
  const [inStockOnly, setInStockOnly] = useState(false);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (inStockOnly && row.isOutOfStock) return false;
      if (!term) return true;
      return (
        row.name.toLowerCase().includes(term) ||
        row.code.toLowerCase().includes(term)
      );
    });
  }, [rows, search, inStockOnly]);

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 pb-3 pt-1">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-9 pl-9 text-sm"
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={inStockOnly}
            onChange={(e) => setInStockOnly(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          In stock only
        </label>
      </div>

      {/* Column header */}
      <div
        className={`${GRID_COLS} border-b py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-800`}
      >
        <div>Shade</div>
        <div className="text-center">Stock</div>
        <div className="text-center">Price</div>
        <div className="text-center">Quantity</div>
        <div className="text-right">Subtotal</div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {isError ? (
          <p className="px-4 py-8 text-center text-sm text-red-600">
            Failed to load variants. Please try again later.
          </p>
        ) : isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <VariantRowSkeleton key={i} />
          ))
        ) : filteredRows.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500">
            No variants match your filters.
          </p>
        ) : (
          <ul className="divide-y dark:divide-gray-800">
            {filteredRows.map((row) => {
              const quantity = quantities[row.variantId] ?? 0;
              const subtotal = row.priceAmount * quantity;

              return (
                <li key={row.variantId} className={`${GRID_COLS} py-2.5`}>
                  {/* Shade name + code */}
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50">
                      <ProductThumbnailImage
                        src={row.image || productThumbnail}
                        alt={row.name}
                        className="h-full w-full object-cover"
                        fallback={<Package className="h-4 w-4 text-gray-400" />}
                      />
                    </div>
                    <div className="min-w-0">
                      <TruncateTooltip
                        as="p"
                        className="text-sm font-medium text-gray-900 dark:text-gray-100"
                      >
                        {row.name}
                      </TruncateTooltip>
                      {row.code && (
                        <TruncateTooltip
                          as="p"
                          className="text-xs text-gray-400"
                        >
                          {row.code}
                        </TruncateTooltip>
                      )}
                    </div>
                  </div>

                  {/* Stock */}
                  <div className="flex justify-center">
                    {row.isOutOfStock ? (
                      <Badge variant="critical" className="text-[10px]">
                        Out
                      </Badge>
                    ) : (
                      <Badge variant="success" className="text-[10px]">
                        In stock
                      </Badge>
                    )}
                  </div>

                  {/* Price */}
                  <div className="text-center text-sm tabular-nums text-gray-700 dark:text-gray-300">
                    {row.priceFormatted}
                  </div>

                  {/* Quantity */}
                  <div className="flex justify-center">
                    {row.isOutOfStock ? (
                      <span className="text-sm text-gray-300">—</span>
                    ) : (
                      <QuantityControl
                        quantity={quantity}
                        onQuantityChange={(q) =>
                          onQuantityChange(row.variantId, q)
                        }
                        min={0}
                        size="sm"
                      />
                    )}
                  </div>

                  {/* Subtotal */}
                  <TruncateTooltip
                    as="div"
                    className="text-right text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100"
                  >
                    {quantity > 0 ? formatPrice(subtotal) : "—"}
                  </TruncateTooltip>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
